import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const AboutPage: React.FC = () => {
  return (
    <div style={{ padding: '2rem 0' }}>
      <Helmet>
        <title>About ChooseAI — Independent AI Tool Discovery & Comparison</title>
        <meta name="description" content="ChooseAI is an independent platform helping founders, creators, and small businesses navigate the AI landscape. Learn about our mission, what we cover, and how to get in touch." />
        <meta property="og:title" content="About ChooseAI — Independent AI Tool Discovery & Comparison" />
        <meta property="og:description" content="ChooseAI is an independent platform helping founders, creators, and small businesses navigate the AI landscape. Learn about our mission, what we cover, and how to get in touch." />
        <meta property="og:url" content="https://chooseai.co.uk/about" />
        <link rel="canonical" href="https://chooseai.co.uk/about" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "AboutPage",
          "name": "About ChooseAI",
          "url": "https://chooseai.co.uk/about",
          "description": "ChooseAI is an independent platform helping founders, creators, and small businesses navigate the AI landscape.",
          "publisher": {
            "@type": "Organization",
            "name": "ChooseAI",
            "url": "https://chooseai.co.uk",
            "logo": "https://chooseai.co.uk/chooseai_logos/chooseai-banner.png",
            "email": "hello@chooseai.co.uk",
            "sameAs": ["https://www.linkedin.com/in/choose-ai"]
          }
        })}</script>
      </Helmet>

      <div className="container">
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>

          {/* Breadcrumb */}
          <div style={{ marginBottom: 'var(--space-lg)' }}>
            <Link to="/" style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
              ← Back to Home
            </Link>
          </div>

          {/* Logo */}
          <div style={{ textAlign: 'center', margin: '3rem 0' }}>
            <img
              src="/chooseai_logos/chooseai-banner-no-bg-dark-tight.png"
              alt="ChooseAI"
              style={{ height: '100px' }}
            />
          </div>

          <div style={{ lineHeight: 1.8, fontSize: '1.05rem' }}>

            <p style={{ marginBottom: 'var(--space-lg)', fontSize: '1.2rem', color: 'var(--color-text-secondary)' }}>
              Building your AI stack shouldn't feel like guesswork.
            </p>

            <p style={{ marginBottom: 'var(--space-lg)' }}>
              ChooseAI is an independent platform built for founders, creators, and small business owners who want to make smarter decisions about the AI tools they use. The AI landscape moves fast — new tools launch constantly, pricing changes, and capabilities evolve. We exist to cut through the noise.
            </p>

            <h2 style={{ marginTop: 'var(--space-xl)', marginBottom: 'var(--space-md)' }}>What We Do</h2>

            <p style={{ marginBottom: 'var(--space-md)' }}>
              We provide two core resources:
            </p>

            <ul style={{ marginBottom: 'var(--space-lg)', paddingLeft: '2rem' }}>
              <li style={{ marginBottom: 'var(--space-sm)' }}>
                <strong>ChooseTools</strong> — detailed, data-backed profiles of the leading AI tools across writing, video, audio, coding, design, CRM, and more. Each profile covers pricing, key features, pros and cons, ease of use, integrations, and who the tool is genuinely best for.
              </li>
              <li style={{ marginBottom: 'var(--space-sm)' }}>
                <strong>ChooseNews</strong> — practical guides, real-world workflows, and in-depth breakdowns of how to apply AI tools to actual business and creative problems. No hype, no filler.
              </li>
            </ul>

            <h2 style={{ marginTop: 'var(--space-xl)', marginBottom: 'var(--space-md)' }}>Our Approach</h2>

            <p style={{ marginBottom: 'var(--space-lg)' }}>
              We focus on clarity over hype, practical use over buzzwords, and real-world value over theoretical potential. Our tool profiles are based on publicly available information, verified pricing pages, and hands-on testing. We don't accept payment to feature or promote tools — our job is to help you find what actually fits your needs, not what pays the most.
            </p>

            <h2 style={{ marginTop: 'var(--space-xl)', marginBottom: 'var(--space-md)' }}>Get in Touch</h2>

            <p style={{ marginBottom: 'var(--space-lg)' }}>
              We're always open to feedback, tool suggestions, partnership enquiries, or just a conversation about the AI landscape.
            </p>

            <ul style={{ listStyle: 'none', padding: 0, marginBottom: 'var(--space-xl)' }}>
              <li style={{ marginBottom: 'var(--space-sm)' }}>
                <strong>Email:</strong>{' '}
                <a href="mailto:hello@chooseai.co.uk" style={{ color: 'var(--color-primary)' }}>
                  hello@chooseai.co.uk
                </a>
              </li>
              <li style={{ marginBottom: 'var(--space-sm)' }}>
                <strong>LinkedIn:</strong>{' '}
                <a href="https://www.linkedin.com/in/choose-ai" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                  linkedin.com/in/choose-ai
                </a>
              </li>
            </ul>

            <div style={{ textAlign: 'center', marginTop: 'var(--space-xl)' }}>
              <Link to="/" className="button button-primary">
                Explore ChooseAI
              </Link>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
